import React from 'react';
import PropTypes from 'prop-types';

import {dispatch, connect} from 'utils/state_manager';

import {Grid, screenSizeSmaller} from 'views/components';

import ClientLeadFeed from './client_lead_scenes/client_lead_feed/ClientLeadFeed';
import ClientLeadSettings from './client_lead_scenes/client_lead_settings/ClientLeadSettings';

import backend from 'utils/backend';

const ClientLeadView = (props) => {
  const [refresh, setRefresh] = React.useState(1);

  const [clientLeadSettingsView, setClientLeadSettingsView] = React.useState('CONTACT');
  const [openDrawerView, setOpenDrawerView] = React.useState(false);

  const [clientLeadData, setClientLeadData] = React.useState();
  const [clientLeadMessages, setClientLeadMessage] = React.useState({});
  const [clientLeadLogs, setClientLeadLogs] = React.useState({});

  const [clientLeadMessagesLastEvaluatedKey, setClientLeadMessagesLastEvaluatedKey] = React.useState(null);
  const [clientLeadLogsLastEvaluatedKey, setClientLeadLogsLastEvaluatedKey] = React.useState(null);

  const [clientLeadDataLoaded, setClientLeadDataLoaded] = React.useState(false);
  const [clientLeadMessagesLoading, setClientLeadMessagesLoading] = React.useState(false);
  const [clientLeadLogsLoading, setClientLeadLogsLoading] = React.useState(false);

  const progressWrap = props.statusProgressWrap;
  const clientLeadRn = props.clientLeadRn;
  const requester = props.requester;

  const requesterRn = requester.userRn;

  const showSettings = screenSizeSmaller('md');

  // Loading clientLead information
  React.useEffect(() => {
    clientLeadBackend.load.loadClientLeadData(clientLeadRn);
  }, [clientLeadRn, refresh]);
  React.useEffect(() => {
    clientLeadBackend.load.loadMessages(clientLeadRn, null);
  }, [clientLeadRn, refresh]);
  React.useEffect(() => {
    clientLeadBackend.load.loadLogs(clientLeadRn, null);
  }, [clientLeadRn, refresh]);

  const clientLeadBackend = {
    load: {
      loadClientLeadData: async (clientLeadRn) => {
        progressWrap(async () => {
          const output = await backend.clientLead.getClientLead(clientLeadRn, true);

          setClientLeadData(output.clientLeadDetail);
          setClientLeadDataLoaded(true);
        }, {debugString: 'CLIENT_leadLOAD'});
      },
      loadMessages: async (clientLeadRn, clientLeadMessagesLastEvaluatedKey) => {
        progressWrap(async () => {
          setClientLeadMessagesLoading(true);
          const output = await backend.clientLead.message.load(clientLeadRn, clientLeadMessagesLastEvaluatedKey, true, true);

          setClientLeadMessage((messages) => {
            const newMessages = output.messages.reduce((result, message) => {
              return {
                ...result,
                [message.messageRn]: message,
              };
            }, {});

            return {
              ...messages,
              ...newMessages,
            };
          });
          setClientLeadMessagesLastEvaluatedKey(output.lastEvaluatedKey);
          setClientLeadMessagesLoading(false);
        }, {debugString: 'CLIENT_leadMESSAGE_LOAD'});
      },
      loadLogs: async (clientLeadRn, clientLeadLogsLastEvaluatedKey) => {
        progressWrap(async () => {
          setClientLeadLogsLoading(true);
          const output = await backend.clientLead.loadLogs(clientLeadRn, clientLeadLogsLastEvaluatedKey, true);

          setClientLeadLogs((logs) => {
            const newLogs = output.logs.reduce((result, log) => {
              return {
                ...result,
                [log.clientLeadLogRn]: log,
              };
            }, {});

            return {
              ...logs,
              ...newLogs,
            };
          });
          setClientLeadLogsLastEvaluatedKey(output.lastEvaluatedKey);
          setClientLeadLogsLoading(false);
        }, {debugString: 'CLIENT_leadLOGS_LOAD'});
      },
    },
    detail: {
      editDescription: async (newBody, onSuccess, onError=()=>undefined) => {
        progressWrap(async () => {
          try {
            const body = {
              description: {
                blob: newBody,
                editorType: 'MARKDOWN',
              },
            };
            const {clientLeadDetail, updateLog} = await backend.clientLead.updateDescription(clientLeadRn, body);

            // Refresh
            setClientLeadData(clientLeadDetail);

            setClientLeadLogs((logs) => {
              return {
                ...logs,
                [updateLog.clientLeadLogRn]: updateLog,
              };
            });

            onSuccess();
          } catch (error) {
            onError();
            console.error(error);
            throw new Error(error);
          }
        }, {debugString: 'CLIENT_leadEDIT_DESCRIPTION'});
      },
      editPhoto: async (file, onSuccess, onError=()=>undefined) => {
        progressWrap(async () => {
          try {
            const type = file.type;
            const name = file.name;
            const body = {
              fileType: type,
              fileName: name,
            };
            const {clientLeadDetail, signedUrl, updateLog} = await backend.clientLead.updateFile(clientLeadRn, body);

            const uploadResult = await backend.s3.upload(signedUrl, file);

            if (uploadResult.status === 200) {
              setClientLeadData(clientLeadDetail);
            } else {
              console.error('Upload failed with status', result.status);
            }

            setClientLeadLogs((logs) => {
              return {
                ...logs,
                [updateLog.clientLeadLogRn]: updateLog,
              };
            });

            onSuccess();
          } catch (error) {
            onError();
            console.error(error);
            throw new Error(error);
          }
        }, {debugString: 'CLIENT_leadEDIT_PHOTO'});
      },
      editStatus: async (status, onSuccess, onError=()=>undefined) => {
        progressWrap(async () => {
          try {
            const body = {
              status,
            };
            const {clientLeadDetail, updateLog} = await backend.clientLead.updateStatus(clientLeadRn, body);

            // Refresh
            setClientLeadData(clientLeadDetail);

            setClientLeadLogs((logs) => {
              return {
                ...logs,
                [updateLog.clientLeadLogRn]: updateLog,
              };
            });

            onSuccess();
          } catch (error) {
            onError();
            console.error(error);
            throw new Error(error);
          }
        }, {debugString: 'CLIENT_leadEDIT_STATUS'});
      },
      editName: async (clientLeadName, onSuccess, onError=()=>undefined) => {
        progressWrap(async () => {
          try {
            const body = {
              clientLeadName,
            };
            const {clientLeadDetail, updateLog} = await backend.clientLead.updateName(clientLeadRn, body);

            // Refresh
            setClientLeadData(clientLeadDetail);

            setClientLeadLogs((logs) => {
              return {
                ...logs,
                [updateLog.clientLeadLogRn]: updateLog,
              };
            });

            onSuccess();
          } catch (error) {
            onError();
            console.error(error);
            throw new Error(error);
          }
        }, {debugString: 'CLIENT_leadEDIT_NAME'});
      },
      addContact: async (type, detail, onSuccess, onError=()=>undefined) => {
        progressWrap(async () => {
          try {
            const body = {
              type,
              detail,
            };
            const {clientLeadDetail, updateLog} = await backend.clientLead.addContact(clientLeadRn, body);

            // Refresh
            setClientLeadData(clientLeadDetail);

            setClientLeadLogs((logs) => {
              return {
                ...logs,
                [updateLog.clientLeadLogRn]: updateLog,
              };
            });

            onSuccess();
          } catch (error) {
            onError();
            console.error(error);
            throw new Error(error);
          }
        }, {debugString: 'CLIENT_leadADD_CONTACT'});
      },
      removeContact: async (type, onSuccess, onError=()=>undefined) => {
        progressWrap(async () => {
          try {
            const body = {
              type,
            };
            const {clientLeadDetail, updateLog} = await backend.clientLead.removeContact(clientLeadRn, body);

            // Refresh
            setClientLeadData(clientLeadDetail);

            setClientLeadLogs((logs) => {
              return {
                ...logs,
                [updateLog.clientLeadLogRn]: updateLog,
              };
            });

            onSuccess();
          } catch (error) {
            onError();
            console.error(error);
            throw new Error(error);
          }
        }, {debugString: 'CLIENT_leadREMOVE_CONTACT'});
      },
    },
    messages: {
      create: async (messageType, newMessage, onSuccess, onError=()=>undefined) => {
        progressWrap(async () => {
          try {
            const body = {
              body: {
                blob: newMessage,
                editorType: 'MARKDOWN',
              },
              messageType: messageType,
              requester: {
                rn: requesterRn,
              },
            };
            const result = await backend.clientLead.message.create(clientLeadRn, body);

            setClientLeadMessage((messages) => {
              return {
                ...messages,
                [result.messageRn]: result.message,
              };
            });

            if (messageType === 'ANNOUNCEMENTS') {
              setClientLeadData((clientLeadData) => {
                return {
                  ...clientLeadData,
                  latestAnnouncementMessageRn: result.messageRn,
                  latestAnnouncementMessage: result.message,
                };
              });
            }
            onSuccess();
          } catch (error) {
            onError();
            console.error(error);
            throw new Error(error);
          }
        }, {debugString: 'CLIENT_leadMESSAGE_CREATE'});
      },
      update: async (messageRn, newMessage, onSuccess, onError=()=>undefined) => {
        progressWrap(async () => {
          try {
            const body = {
              body: {
                blob: newMessage,
                editorType: 'MARKDOWN',
              },
            };
            const result = await backend.clientLead.message.update(clientLeadRn, messageRn, body);

            setClientLeadMessage((messages) => {
              messages[result.messageRn] = result.message;
              return {
                ...messages,
              };
            });

            if (clientLeadData.latestAnnouncementMessageRn === result.messageRn) {
              setClientLeadData((clientLeadData) => {
                return {
                  ...clientLeadData,
                  latestAnnouncementMessageRn: result.messageRn,
                  latestAnnouncementMessage: result.message,
                };
              });
            }

            onSuccess();
          } catch (error) {
            onError();
            console.error(error);
            throw new Error(error);
          }
        }, {
          debugString: 'TICKET_MESSAGE_UPDATE',
        });
      },
      delete: async (messageRn, onSuccess, onError=()=>undefined) => {
        progressWrap(async () => {
          try {
            const result = await backend.clientLead.message.delete(clientLeadRn, messageRn);

            setClientLeadMessage((messages) => {
              delete messages[result.messageRn];
              return {
                ...messages,
              };
            });

            if (clientLeadData.latestAnnouncementMessageRn === result.messageRn) {
              setClientLeadData((clientLeadData) => {
                return {
                  ...clientLeadData,
                  latestAnnouncementMessageRn: null,
                  latestAnnouncementMessage: null,
                };
              });
            }

            onSuccess();
          } catch (error) {
            onError();
            console.error(error);
            throw new Error(error);
          }
        }, {
          debugString: 'TICKET_MESSAGE_DELETE',
        });
      },
      addEmoji: async (messageRn, emoji, userRn, onSuccess=()=>undefined, onError=()=>undefined) => {
        progressWrap(async () => {
          try {
            const body = {
              emoji: emoji,
              requester: {
                rn: userRn,
              },
            };
            const result = await backend.clientLead.message.emoji.add(clientLeadRn, messageRn, body);

            setClientLeadMessage((messages) => {
              messages[result.messageRn] = result.message;
              return {
                ...messages,
              };
            });

            if (clientLeadData.latestAnnouncementMessageRn === result.messageRn) {
              setClientLeadData((clientLeadData) => {
                return {
                  ...clientLeadData,
                  latestAnnouncementMessageRn: result.messageRn,
                  latestAnnouncementMessage: result.message,
                };
              });
            }

            onSuccess();
          } catch (error) {
            onError();
            console.error(error);
            throw new Error(error);
          }
        }, {
          debugString: 'TICKET_MESSAGE_EMOJI_ADD',
        });
      },
      removeEmoji: async (messageRn, emoji, userRn, onSuccess=()=>undefined, onError=()=>undefined) => {
        progressWrap(async () => {
          try {
            const body = {
              emoji: emoji,
              requester: {
                rn: userRn,
              },
            };
            const result = await backend.clientLead.message.emoji.remove(clientLeadRn, messageRn, body);

            setClientLeadMessage((messages) => {
              messages[result.messageRn] = result.message;
              return {
                ...messages,
              };
            });

            if (clientLeadData.latestAnnouncementMessageRn === result.messageRn) {
              setClientLeadData((clientLeadData) => {
                return {
                  ...clientLeadData,
                  latestAnnouncementMessageRn: result.messageRn,
                  latestAnnouncementMessage: result.message,
                };
              });
            }

            onSuccess();
          } catch (error) {
            onError();
            console.error(error);
            throw new Error(error);
          }
        }, {
          debugString: 'TICKET_MESSAGE_EMOJI_REMOVE',
        });
      },
    },
  };

  if (!clientLeadDataLoaded) return null;

  return (
    <>
      <Grid container style={{'textAlign': 'left'}}>
        <Grid item md={9} xs={12} >
          <ClientLeadFeed
            clientLeadRn={clientLeadRn}
            clientLeadData={clientLeadData}
            clientLeadMessages={clientLeadMessages}
            clientLeadLogs={clientLeadLogs}
            clientLeadBackend={clientLeadBackend}
            refresh={refresh}
            setRefresh={setRefresh}
            showSettings={showSettings}
            setOpenDrawerView={setOpenDrawerView}
            clientLeadMessagesLoading={clientLeadMessagesLoading}
            clientLeadMessagesLastEvaluatedKey={clientLeadMessagesLastEvaluatedKey}
            clientLeadLogsLastEvaluatedKey={clientLeadLogsLastEvaluatedKey}
            clientLeadLogsLoading={clientLeadLogsLoading}
          />
        </Grid>
        <Grid item md={3} pl={{md: 2, xs: 1}}>
          <ClientLeadSettings
            showSettings={showSettings}
            openDrawerView={openDrawerView}
            setOpenDrawerView={setOpenDrawerView}
            clientLeadSettingsView={clientLeadSettingsView}
            setClientLeadSettingsView={setClientLeadSettingsView}
            clientLeadRn={clientLeadRn}
            clientLeadData={clientLeadData}
            clientLeadBackend={clientLeadBackend}
          />
        </Grid>
      </Grid>
    </>
  );
};

ClientLeadView.propTypes = {
  clientLeadRn: PropTypes.string.isRequired,
  statusProgressWrap: PropTypes.func.isRequired,
  requester: PropTypes.object.isRequired,
};

const mapStateToProps = (state) => ({
  requester: state.user,
});

export default connect(mapStateToProps, dispatch)(React.memo(ClientLeadView));
